/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskResultsRevealPayload } from "../funcs/riskResultsRevealPayload.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RiskRevealPayloadResult } from "../models/components/riskrevealpayloadresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevealRiskResultPayloadRequest,
  RevealRiskResultPayloadSecurity,
} from "../models/operations/revealriskresultpayload.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskRevealResultPayloadMutationVariables = {
  request: RevealRiskResultPayloadRequest;
  security?: RevealRiskResultPayloadSecurity | undefined;
  options?: RequestOptions;
};

export type RiskRevealResultPayloadMutationData = RiskRevealPayloadResult;

export type RiskRevealResultPayloadMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revealRiskResultPayload risk
 *
 * @remarks
 * Return the full scanned payload of the MCP tool call phase a risk result was raised on, so its findings can be shown in context. Positions of every finding with the same execution_id and phase index into the returned payload. Requires an unrestricted chat:read grant. Every successful reveal is audited.
 */
export function useRiskRevealResultPayloadMutation(
  options?: MutationHookOptions<
    RiskRevealResultPayloadMutationData,
    RiskRevealResultPayloadMutationError,
    RiskRevealResultPayloadMutationVariables
  >,
): UseMutationResult<
  RiskRevealResultPayloadMutationData,
  RiskRevealResultPayloadMutationError,
  RiskRevealResultPayloadMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskRevealResultPayloadMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskRevealResultPayload(): MutationKey {
  return ["@gram/client", "results", "revealPayload"];
}

export function buildRiskRevealResultPayloadMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskRevealResultPayloadMutationVariables,
  ) => Promise<RiskRevealResultPayloadMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskRevealResultPayload(),
    mutationFn: function riskRevealResultPayloadMutationFn({
      request,
      security,
      options,
    }): Promise<RiskRevealResultPayloadMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskResultsRevealPayload(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
